import React, { useState } from "react";
import { UserProfile } from "../auth/session";
import { ChessBoardViewer } from "../components/ChessBoardViewer";
import { StatCard } from "../components/StatCard";

interface CoachDashboardProps {
  user: UserProfile;
}

interface Student {
  id: string;
  name: string;
  rating: number;
  ratingChange: string;
  batch: string;
  attendance: number;
  lastActive: string;
  blunderRate: number;
  recentAssignmentStatus: "Completed" | "Pending" | "Overdue";
}

const ROSTER: Student[] = [
  {
    id: "stu-01",
    name: "Arjun Kamalendran",
    rating: 1540,
    ratingChange: "+28",
    batch: "Intermediate Batch A",
    attendance: 96,
    lastActive: "Today 14:20",
    blunderRate: 3.2,
    recentAssignmentStatus: "Pending",
  },
  {
    id: "stu-02",
    name: "Vikram Rajan",
    rating: 1520,
    ratingChange: "+12",
    batch: "Intermediate Batch A",
    attendance: 92,
    lastActive: "Yesterday",
    blunderRate: 4.8,
    recentAssignmentStatus: "Completed",
  },
  {
    id: "stu-03",
    name: "Kavitha Murugan",
    rating: 1565,
    ratingChange: "+45",
    batch: "Intermediate Batch A",
    attendance: 100,
    lastActive: "Today 11:15",
    blunderRate: 2.1,
    recentAssignmentStatus: "Completed",
  },
  {
    id: "stu-04",
    name: "Dinesh Pandian",
    rating: 1480,
    ratingChange: "-15",
    batch: "Intermediate Batch B",
    attendance: 84,
    lastActive: "3 days ago",
    blunderRate: 6.4,
    recentAssignmentStatus: "Overdue",
  },
  {
    id: "stu-05",
    name: "Siddharth Sundaram",
    rating: 1580,
    ratingChange: "+32",
    batch: "Intermediate Batch A",
    attendance: 95,
    lastActive: "Today 16:45",
    blunderRate: 2.9,
    recentAssignmentStatus: "Completed",
  },
];

export function CoachDashboard({
  user,
}: CoachDashboardProps): React.JSX.Element {
  const [tab, setTab] = useState<"roster" | "assignments" | "reviews">(
    "roster",
  );
  const [selectedStudent, setSelectedStudent] = useState<Student>(ROSTER[0]!);
  const [assignmentTitle, setAssignmentTitle] = useState("");
  const [assignmentDue, setAssignmentDue] = useState("");
  const [assignmentSuccess, setAssignmentSuccess] = useState(false);

  function handleCreateAssignment(e: React.FormEvent) {
    e.preventDefault();
    if (!assignmentTitle.trim()) return;
    setAssignmentSuccess(true);
    setAssignmentTitle("");
    setAssignmentDue("");
    setTimeout(() => setAssignmentSuccess(false), 3000);
  }

  return (
    <div className="role-dashboard coach-dashboard">
      <div className="coach-welcome-banner">
        <div>
          <h2>Coach Workspace</h2>
          <p>
            Welcome, {user.displayName}. Active Coach at Tamil Nadu Chess
            Academy • FIDE Trainer License: Active
          </p>
        </div>
        <nav className="dashboard-subnav" aria-label="Coach sections">
          <button
            type="button"
            className={`subnav-link ${tab === "roster" ? "active" : ""}`}
            onClick={() => setTab("roster")}
          >
            Student Roster
          </button>
          <button
            type="button"
            className={`subnav-link ${tab === "assignments" ? "active" : ""}`}
            onClick={() => setTab("assignments")}
          >
            Tactical Assignments
          </button>
          <button
            type="button"
            className={`subnav-link ${tab === "reviews" ? "active" : ""}`}
            onClick={() => setTab("reviews")}
          >
            Game Reviews
          </button>
        </nav>
      </div>

      <div className="dashboard-content">
        <div className="stat-grid">
          <StatCard
            label="Active Students"
            value={ROSTER.length}
            subtext="Assigned across 2 Batches"
            icon="👥"
          />
          <StatCard
            label="Games Reviewed"
            value="38"
            subtext="This month"
            trend={{ direction: "up", value: "+14" }}
            icon="🔍"
          />
          <StatCard
            label="Average Attendance"
            value="93.4%"
            subtext="High participation rate"
            icon="📅"
          />
          <StatCard
            label="Tactics Completion"
            value="82%"
            subtext="Curriculum pace: Optimal"
            trend={{ direction: "up", value: "+5%" }}
            icon="🎯"
          />
        </div>

        {tab === "roster" && (
          <div className="coach-roster-layout">
            <div className="roster-table-card">
              <h3>Assigned Students ({ROSTER.length})</h3>
              <div className="table-responsive">
                <table
                  className="data-table"
                  aria-label="Students roster table"
                >
                  <thead>
                    <tr>
                      <th>Student Name</th>
                      <th>Rating</th>
                      <th>Batch</th>
                      <th>Attendance</th>
                      <th>Last Active</th>
                      <th>Recent Assignment</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ROSTER.map((stu) => (
                      <tr
                        key={stu.id}
                        className={
                          selectedStudent.id === stu.id ? "row-selected" : ""
                        }
                      >
                        <td>
                          <strong>{stu.name}</strong>
                        </td>
                        <td>
                          {stu.rating}{" "}
                          <span className="trend-up">({stu.ratingChange})</span>
                        </td>
                        <td>{stu.batch}</td>
                        <td>{stu.attendance}%</td>
                        <td>{stu.lastActive}</td>
                        <td>
                          <span
                            className={`badge badge-${stu.recentAssignmentStatus === "Completed" ? "success" : stu.recentAssignmentStatus === "Pending" ? "accent" : "error"}`}
                          >
                            {stu.recentAssignmentStatus}
                          </span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn-secondary btn-sm"
                            onClick={() => setSelectedStudent(stu)}
                          >
                            Inspect
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="student-profile-summary-card">
              <h3>Student Deep Dive: {selectedStudent.name}</h3>
              <div className="deep-dive-grid">
                <div className="dive-item">
                  <span className="dive-label">Current Rating</span>
                  <span className="dive-val">
                    {selectedStudent.rating} Rapid
                  </span>
                </div>
                <div className="dive-item">
                  <span className="dive-label">Blunder Rate</span>
                  <span className="dive-val">
                    {selectedStudent.blunderRate}% of moves
                  </span>
                </div>
                <div className="dive-item">
                  <span className="dive-label">Key Weakness</span>
                  <span className="dive-val">Endgame King Activity</span>
                </div>
                <div className="dive-item">
                  <span className="dive-label">Key Strength</span>
                  <span className="dive-val">Tactical Pawn Sacrifices</span>
                </div>
              </div>
              <div className="action-buttons-group">
                <button
                  type="button"
                  className="btn-primary btn-sm"
                  onClick={() => setTab("assignments")}
                >
                  Assign Custom Drill
                </button>
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={() => setTab("reviews")}
                >
                  Review Latest Game
                </button>
              </div>
            </div>
          </div>
        )}

        {tab === "assignments" && (
          <div className="coach-assignments-layout">
            <div className="create-assignment-card">
              <h3>Create Tactical Assignment</h3>
              {assignmentSuccess && (
                <div className="alert-box alert-success" role="status">
                  Assignment dispatched to {selectedStudent.name} successfully!
                </div>
              )}
              <form onSubmit={handleCreateAssignment}>
                <div className="form-group">
                  <label htmlFor="assign-student">Target Student</label>
                  <select
                    id="assign-student"
                    value={selectedStudent.id}
                    onChange={(e) => {
                      const s = ROSTER.find((x) => x.id === e.target.value);
                      if (s) setSelectedStudent(s);
                    }}
                  >
                    {ROSTER.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name} ({s.batch})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="assign-title">Assignment Title</label>
                  <input
                    id="assign-title"
                    type="text"
                    required
                    value={assignmentTitle}
                    onChange={(e) => setAssignmentTitle(e.target.value)}
                    placeholder="e.g. Rook & Pawn Endgame Practice (Lucena Position)"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="assign-due">Due Date</label>
                  <input
                    id="assign-due"
                    type="date"
                    required
                    value={assignmentDue}
                    onChange={(e) => setAssignmentDue(e.target.value)}
                  />
                </div>

                <button type="submit" className="btn-primary">
                  Publish Assignment
                </button>
              </form>
            </div>
          </div>
        )}

        {tab === "reviews" && (
          <div className="coach-reviews-layout">
            <div className="section-card">
              <h3>Game Review: {selectedStudent.name} vs Vikram R.</h3>
              <p className="game-meta-text">
                Tamil Nadu State Rapid League • Round 4 • Result: 1-0
              </p>
              <ChessBoardViewer
                fen="r1bqkb1r/pp2pppp/2np1n2/8/3NP3/2N5/PPP2PPP/R1BQKB1R w KQkq - 1 6"
                moves={[
                  "1. e4 c5",
                  "2. Nf3 d6",
                  "3. d4 cxd4",
                  "4. Nxd4 Nf6",
                  "5. Nc3 Nc6",
                ]}
                orientation="white"
              />
              <div className="coach-commentary-box">
                <h4>Coach Ramesh's Commentary</h4>
                <p>
                  "Arjun found the key tactical stroke with 4...Nf6 and
                  developed with tempo. Notice that Black's bishop on c8 remains
                  passive until d5 is secured. Good discipline."
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
